import type { AdminRole } from '@/admin/adminApi';

/**
 * Ce que l'ecran « Administrateurs » affiche d'autre que la liste : la commande
 * d'octroi et la matrice des droits. Module pur, teste sans rendu.
 */

/**
 * Commande d'octroi **de production**, a jouer sur le VPS dans le depot clone
 * (`docs/deployment/vps.md`, « Accorder un rôle d'administration »).
 *
 * `--env-file .env.prod` n'est pas decoratif : `docker compose` ne charge
 * automatiquement que `.env`, jamais `.env.prod`, et sans lui le fichier
 * Compose de production refuse de demarrer (variables obligatoires absentes).
 * Les `<…>` sont a remplacer par l'operateur : la commande n'est pas
 * executable telle quelle, et c'est voulu.
 */
export const GRANT_COMMAND =
  'docker compose --env-file .env.prod -f docker-compose.prod.yml exec backend php bin/console app:admin:grant <adresse> <support|admin>';

/** Phrase de principe, affichee sous le titre (ADR 0032, point 4). */
export const CONSOLE_ONLY_NOTICE =
  'Les rôles s’accordent en console sur le serveur, jamais depuis cet écran : un écran qui crée un administrateur peut en créer un tout seul (ADR 0032).';

/** Retrait : meme canal, meme raison. Affiche en aide sous la commande d'octroi. */
export const REVOKE_HINT = 'Le retrait passe par la commande jumelle app:admin:revoke <adresse>.';

/** Libelles courts pour un tableau : `ROLE_LABELS` precise « (lecture seule) ». */
export const ROLE_NAMES: Readonly<Record<AdminRole, string>> = {
  admin: 'Administrateur',
  support: 'Support',
};

export interface PermissionRow {
  readonly id: string;
  readonly label: string;
  /** Plus petit role qui l'exerce : `support` lit, `admin` lit et agit. */
  readonly minimumRole: AdminRole;
}

/**
 * Matrice **statique** des droits, relue des gardes de controleur
 * (`AdminAccessGuard::require`) au moment de la livraison de BO-13 :
 *
 * - `support` : comptes et fiches, profil RGPD et liste des exports, registre des
 *   demandes RGPD (lecture), journal et registre des acces, sante, traitements en erreur, reglages, pilotage,
 *   explorateur de donnees (sans texte libre), recherche, tracabilite et
 *   integrite du stock (sans texte libre, quel que soit le role) ;
 * - `admin` : en plus, texte libre de l'explorateur, deconnexion d'un compte,
 *   export (et son telechargement), export du journal, anonymisation, rejeu,
 *   modification d'un reglage, correction des documents legaux, saisie et
 *   evolution d'une demande RGPD.
 *
 * **Elle peut deriver du code.** Aucun test ne relit les controleurs : toute
 * nouvelle action d'administration doit mettre cette liste a jour (consigne
 * dans `docs/architecture/backoffice-admin.md`). La « suspension » d'un compte,
 * que la maquette annonce, n'est servie par aucune route : elle n'est pas
 * listee, une permission inexistante ne s'affiche pas.
 */
export const PERMISSIONS: readonly PermissionRow[] = [
  { id: 'sync-supervision', label: 'Consulter les tentatives et l’historique des synchronisations', minimumRole: 'support' },
  { id: 'sync-recovery', label: 'Demander une relance ou une resynchronisation ciblée (avec mot de passe)', minimumRole: 'admin' },
  { id: 'accounts', label: 'Consulter les comptes et leurs profils bébé', minimumRole: 'support' },
  { id: 'privacy', label: 'Lire le profil RGPD d’un compte et la liste des exports', minimumRole: 'support' },
  { id: 'privacy-requests-read', label: 'Lire le registre des demandes RGPD et ses effectifs', minimumRole: 'support' },  { id: 'audit', label: 'Lire le journal d’audit et le registre des accès', minimumRole: 'support' },
  { id: 'health', label: 'Lire la santé du service et les traitements en erreur', minimumRole: 'support' },
  { id: 'settings-read', label: 'Lire les réglages globaux', minimumRole: 'support' },
  { id: 'pilotage', label: 'Lire les indicateurs de pilotage et les statistiques d’usage', minimumRole: 'support' },
  { id: 'explorer', label: 'Explorer les données d’un profil bébé (sans texte libre)', minimumRole: 'support' },
  { id: 'stock-trace', label: 'Retrouver un lot, reconstituer sa traçabilité et contrôler l’intégrité du stock', minimumRole: 'support' },
  { id: 'free-text', label: 'Lire le texte libre des enregistrements (notes)', minimumRole: 'admin' },
  { id: 'sessions', label: 'Déconnecter les appareils d’un compte', minimumRole: 'admin' },
  { id: 'export', label: 'Exporter les données d’un compte et télécharger l’export', minimumRole: 'admin' },
  { id: 'audit-export', label: 'Exporter le journal d’audit (CSV)', minimumRole: 'admin' },
  { id: 'anonymize', label: 'Anonymiser un compte', minimumRole: 'admin' },
  { id: 'retry', label: 'Rejouer un traitement en erreur', minimumRole: 'admin' },
  { id: 'settings-write', label: 'Modifier un réglage global', minimumRole: 'admin' },
  { id: 'legal', label: 'Corriger les documents légaux (CGU, CGV)', minimumRole: 'admin' },
  { id: 'privacy-requests-write', label: 'Enregistrer et faire évoluer une demande RGPD', minimumRole: 'admin' },
];

/** `admin` satisfait `support`, jamais l'inverse (meme ordre que `roleSatisfies`). */
export const roleCan = (role: AdminRole, permission: PermissionRow): boolean =>
  role === 'admin' || permission.minimumRole === 'support';

const DATE_FORMAT = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' });

/**
 * « Depuis » : date seule, ou « — » quand l'audit n'en garde pas.
 *
 * Une chaine illisible rend aussi « — » plutot que « Invalid Date ».
 */
export const formatGrantDate = (iso: string | null): string | null => {
  if (iso === null || iso === '') {
    return null;
  }

  const date = new Date(iso);

  return Number.isNaN(date.getTime()) ? null : DATE_FORMAT.format(date);
};
